<template>
  <nav class="sale-mode-links" data-testid="sale-mode-links">
    <RouterLink
      v-for="link in links"
      :key="link.mode"
      :to="{ name: link.routeName }"
      :class="{ 'is-active': link.mode === current }"
      :aria-current="link.mode === current ? 'page' : undefined"
      :data-testid="`sale-mode-${link.mode}`"
    >
      {{ $t(link.labelKey) }}
    </RouterLink>
  </nav>
  <RouterLink :to="{ name: 'ForwardsPapDealNew' }" class="btn btn-link">
    {{ $t("forwards.picker.new_pap_deal") }}
  </RouterLink>
</template>

<script setup lang="ts">
/**
 * The heading links staff get on both "new sale" pages: the spot sale of existing inventory, the
 * volume picker for future inventory, and the pay-as-produced wizard.
 *
 * The first two are separate routes, so the pair looks like the segmented control it replaced but
 * navigates rather than toggles. Callers render it for `readsForwardsAsStaff`: a sales admin cuts no
 * deal, but opens both deal pages read-only (user decisions 2026-09-17, 2026-09-28).
 */

defineProps<{
  current: "spot" | "forward";
}>();

const links = [
  {
    mode: "spot",
    routeName: "AdminTransactionsNewView",
    labelKey: "forwards.picker.mode.existing_inventory",
  },
  {
    mode: "forward",
    routeName: "AdminTransactionsNewForwardView",
    labelKey: "forwards.picker.mode.future_inventory",
  },
] as const;
</script>

<style scoped lang="scss">
// Mirrors SegmentedControl's default look, which this pair replaced; the heading row needs it to
// stay a control, not a banner.
.sale-mode-links {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  width: 22rem;
  background: var(--phoenix-gray-100);
  padding: 4px;
  border-radius: 10px;
  gap: 2px;

  a {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 9px 12px;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    color: var(--phoenix-gray-600);
    text-decoration: none;
    text-align: center;
    transition: all 0.12s ease;

    &:hover {
      color: var(--phoenix-gray-1000);
    }

    &.is-active {
      background: #ffffff;
      color: var(--phoenix-gray-1000);
      box-shadow:
        0 1px 2px rgba(var(--phoenix-gray-1000-rgb), 0.04),
        0 0 0 1px var(--phoenix-border-color-translucent);
    }
  }
}
</style>
